/**
 * @fileoverview Idea data model with JSDoc types
 * All ideas entering the frontend MUST conform to this shape
 *
 * Part of Phase 1: Data Boundary Refactoring
 * @version 1.0.0
 */

/**
 * @typedef {Object} Idea
 * @property {string} id - UUID
 * @property {string} sessionId - Parent session UUID
 * @property {string} format - PRINT|SOCIAL|VIDEO|DIGITAL|EXPERIENTIAL|CAMPAIGN
 * @property {string} triggerCode - Trigger code (A1-O4)
 * @property {string} triggerName - Trigger display name
 * @property {string} categoryCode - Category code (A-O)
 * @property {string} categoryName - Category display name
 * @property {string} categoryColor - Category hex color
 * @property {string} headline - Main headline
 * @property {string} visual - Visual description
 * @property {string} whyItWorks - Explanation of effectiveness
 * @property {string|null} imageUrl - Generated image URL (R2)
 * @property {string|null} imagePrompt - Prompt used for image
 * @property {string|null} videoUrl - Generated video URL (R2)
 * @property {string|null} videoPrompt - Prompt used for video
 * @property {boolean} saved - User saved this idea (alias for isSaved)
 * @property {boolean} isSaved - User saved this idea
 * @property {boolean} isRefined - Has been refined via Art Director
 * @property {number} version - Version number for refinements
 * @property {string} createdAt - ISO timestamp
 * @property {string|null} updatedAt - ISO timestamp
 * @property {Object} meta - Format-specific fields
 */

/**
 * Creates a normalized Idea object from raw API response
 * @param {Object} raw - Raw API response (snake_case)
 * @returns {Idea|null} - Normalized idea
 */
export function normalizeIdea(raw) {
  if (!raw) return null;

  // Extract meta fields
  const meta = raw.meta || {};

  // Determine saved state - check multiple sources
  const savedState = Boolean(
    raw.is_saved ??
    raw.isSaved ??
    raw.saved ??
    false
  );

  const formatMeta = extractFormatMeta(raw.format, meta, raw);

  const normalized = {
    // Core fields
    id: String(raw.id || ''),
    sessionId: raw.session_id || raw.sessionId || null,
    format: (raw.format || 'print').toLowerCase(),

    // Trigger info
    triggerCode: raw.trigger_code || raw.triggerCode || raw.trigger || '',
    triggerName: raw.trigger_name || raw.triggerName || meta.triggerName || '',
    categoryCode: raw.category_code || raw.categoryCode || (raw.trigger_code || raw.triggerCode || '')[0] || '',
    categoryName: raw.category_name || raw.categoryName || meta.categoryName || '',
    categoryColor: raw.category_color || raw.categoryColor || meta.categoryColor || '#000000',

    // Content fields (with fallbacks for legacy data)
    headline: raw.headline || meta.headline || '',
    visual: raw.visual || raw.visual_description || raw.text || meta.visual || '',
    whyItWorks: raw.why_it_works || raw.whyItWorks || raw.explanation || meta.whyItWorks || '',

    // Media URLs
    imageUrl: raw.image_url || raw.imageUrl || meta.imageUrl || null,
    imagePrompt: raw.image_prompt || raw.imagePrompt || meta.imagePrompt || null,
    videoUrl: raw.video_url || raw.videoUrl || meta.videoUrl || null,
    videoPrompt: raw.video_prompt || raw.videoPrompt || meta.videoPrompt || null,

    // State flags (provide both for backward compat)
    saved: savedState,
    isSaved: savedState,
    isRefined: Boolean(raw.is_refined ?? raw.isRefined ?? false),
    version: raw.version || 1,

    // Source/refinement fields
    sourceFragment: raw.source_fragment || raw.sourceFragment || null,
    sourceTruth: raw.source_truth || raw.sourceTruth || null,

    // Timestamps
    createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
    updatedAt: raw.updated_at || raw.updatedAt || null,
    savedAt: raw.saved_at || raw.savedAt || null,

    // Format-specific meta (pass through but extract common fields)
    meta: formatMeta,

    // Promote format-specific fields to top level for easy access
    // VIDEO
    ...(formatMeta.scenes != null && { scenes: formatMeta.scenes }),
    ...(formatMeta.sceneCount != null && { sceneCount: formatMeta.sceneCount }),
    ...(formatMeta.duration != null && { duration: formatMeta.duration }),
    ...(formatMeta.style != null && { style: formatMeta.style }),
    ...(formatMeta.super != null && { super: formatMeta.super }),
    ...(formatMeta.alternativeSupers != null && { alternativeSupers: formatMeta.alternativeSupers }),
    // SOCIAL
    ...(formatMeta.hook != null && { hook: formatMeta.hook }),
    ...(formatMeta.sequence != null && { sequence: formatMeta.sequence }),
    ...(formatMeta.caption != null && { caption: formatMeta.caption }),
    ...(formatMeta.alternativeHooks != null && { alternativeHooks: formatMeta.alternativeHooks }),
    // DIGITAL
    ...(formatMeta.frames != null && { frames: formatMeta.frames }),
    ...(formatMeta.interaction != null && { interaction: formatMeta.interaction }),
    ...(formatMeta.cta != null && { cta: formatMeta.cta }),
    ...(formatMeta.alternativeCTAs != null && { alternativeCTAs: formatMeta.alternativeCTAs }),
    // EXPERIENTIAL
    ...(formatMeta.experience != null && { experience: formatMeta.experience }),
    ...(formatMeta.setup != null && { setup: formatMeta.setup }),
    ...(formatMeta.participation != null && { participation: formatMeta.participation }),
    ...(formatMeta.reveal != null && { reveal: formatMeta.reveal }),
    ...(formatMeta.participantJourney != null && { participantJourney: formatMeta.participantJourney }),
    ...(formatMeta.keyMoments != null && { keyMoments: formatMeta.keyMoments }),
    ...(formatMeta.alternativeReveals != null && { alternativeReveals: formatMeta.alternativeReveals }),
    // CAMPAIGN
    ...(formatMeta.campaignLine != null && { campaignLine: formatMeta.campaignLine }),
    ...(formatMeta.manifesto != null && { manifesto: formatMeta.manifesto }),
    ...(formatMeta.extensions != null && { extensions: formatMeta.extensions }),
    ...(formatMeta.phases != null && { phases: formatMeta.phases }),
    ...(formatMeta.channels != null && { channels: formatMeta.channels }),
    ...(formatMeta.timeline != null && { timeline: formatMeta.timeline }),
    ...(formatMeta.alternativeLines != null && { alternativeLines: formatMeta.alternativeLines }),

    // Legacy aliases for backward compatibility
    trigger: raw.trigger_code || raw.triggerCode || raw.trigger || '',
    text: raw.visual || raw.visual_description || raw.text || meta.visual || '',
    explanation: raw.why_it_works || raw.whyItWorks || raw.explanation || meta.whyItWorks || '',

    // Generator-specific fields
    idea: meta.idea || raw.idea || '',
    title: meta.title || raw.title || raw.headline || '',
    headlineLabel: meta.headlineLabel || raw.headlineLabel || 'HEADLINE',
    sectionLabel: meta.sectionLabel || raw.sectionLabel || 'VISUAL',
    alternativeHeadlines: meta.alternativeHeadlines || raw.alternativeHeadlines || [],
    refinementCount: meta.refinementCount || raw.refinementCount || 0,
    critique: meta.critique || raw.critique || null,

    // Brand/product context
    brandId: raw.brand_id || raw.brandId || null,
    productId: raw.product_id || raw.productId || null,

    // Scores if present
    scores: raw.scores || meta.scores || null
  };

  // Development-only validation
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    validateIdea(normalized);
  }

  return normalized;
}

/**
 * Extract format-specific meta fields
 * @param {string} format
 * @param {Object} meta
 * @param {Object} raw
 * @returns {Object}
 */
function extractFormatMeta(format, meta, raw) {
  const result = { ...meta };

  // Remove fields that are now top-level
  delete result.headline;
  delete result.visual;
  delete result.whyItWorks;
  delete result.imageUrl;
  delete result.videoUrl;
  delete result.triggerName;
  delete result.categoryName;
  delete result.categoryColor;

  // Format-specific extraction
  switch ((format || '').toUpperCase()) {
    case 'VIDEO':
      result.scenes = meta.scenes || raw.scenes || [];
      result.sceneCount = meta.sceneCount || meta.scene_count || result.scenes?.length || 0;
      result.duration = meta.duration || raw.duration || null;
      result.style = meta.style || raw.style || '';
      result.super = meta.super || raw.super || '';
      result.alternativeSupers = meta.alternativeSupers || meta.alternative_supers || [];
      break;

    case 'SOCIAL':
      result.hook = meta.hook || raw.hook || '';
      result.sequence = meta.sequence || raw.sequence || [];
      result.caption = meta.caption || raw.caption || '';
      result.alternativeHooks = meta.alternativeHooks || meta.alternative_hooks || [];
      break;

    case 'DIGITAL':
      result.frames = meta.frames || raw.frames || [];
      result.interaction = meta.interaction || raw.interaction || '';
      result.cta = meta.cta || raw.cta || '';
      result.alternativeCTAs = meta.alternativeCTAs || meta.alternative_ctas || [];
      break;

    case 'EXPERIENTIAL':
      result.experience = meta.experience || raw.experience || '';
      result.setup = meta.setup || raw.setup || '';
      result.participation = meta.participation || raw.participation || '';
      result.reveal = meta.reveal || raw.reveal || '';
      result.participantJourney = meta.participantJourney || meta.participant_journey || [];
      result.keyMoments = meta.keyMoments || meta.key_moments || [];
      result.alternativeReveals = meta.alternativeReveals || meta.alternative_reveals || [];
      break;

    case 'CAMPAIGN':
      result.campaignLine = meta.campaignLine || meta.campaign_line || raw.campaignLine || '';
      result.manifesto = meta.manifesto || raw.manifesto || '';
      result.extensions = meta.extensions || raw.extensions || '';
      result.phases = meta.phases || raw.phases || [];
      result.channels = meta.channels || raw.channels || [];
      result.timeline = meta.timeline || raw.timeline || '';
      result.alternativeLines = meta.alternativeLines || meta.alternative_lines || [];
      break;
  }

  return result;
}

/**
 * Validate idea has all required fields (dev mode only)
 * @param {Idea} idea
 */
function validateIdea(idea) {
  const required = ['id', 'format'];
  const missing = required.filter(field => !idea[field]);

  if (missing.length > 0) {
    console.warn(`[Idea Validation] Missing required fields: ${missing.join(', ')}`, idea);
  }

  // Warn about type issues
  if (idea.sessionId && typeof idea.sessionId !== 'string') {
    console.warn(`[Idea Validation] sessionId should be string, got: ${typeof idea.sessionId}`);
  }
}

/**
 * Normalize array of ideas
 * @param {Object[]} rawIdeas
 * @returns {Idea[]}
 */
export function normalizeIdeas(rawIdeas) {
  if (!Array.isArray(rawIdeas)) return [];
  return rawIdeas.map(normalizeIdea).filter(Boolean);
}

/**
 * Convert normalized Idea back to database format for saving
 * @param {Idea} idea
 * @returns {Object} - snake_case format for API
 */
export function ideaToDb(idea) {
  if (!idea) return null;

  return {
    id: idea.id,
    session_id: idea.sessionId,
    format: idea.format,
    trigger_code: idea.triggerCode,
    headline: idea.headline,
    visual: idea.visual,
    why_it_works: idea.whyItWorks,
    image_url: idea.imageUrl,
    image_prompt: idea.imagePrompt,
    video_url: idea.videoUrl,
    video_prompt: idea.videoPrompt,
    is_saved: idea.saved || idea.isSaved,
    is_refined: idea.isRefined,
    version: idea.version,
    source_fragment: idea.sourceFragment,
    source_truth: idea.sourceTruth,
    brand_id: idea.brandId,
    product_id: idea.productId,
    scores: idea.scores,
    meta: {
      ...idea.meta,
      // Include common fields that backend might expect in meta
      triggerName: idea.triggerName,
      categoryName: idea.categoryName,
      categoryColor: idea.categoryColor,
      idea: idea.idea,
      title: idea.title,
      headlineLabel: idea.headlineLabel,
      sectionLabel: idea.sectionLabel,
      alternativeHeadlines: idea.alternativeHeadlines,
      refinementCount: idea.refinementCount,
      critique: idea.critique,
      // Pack format-specific top-level fields back into meta
      ...(idea.scenes != null && { scenes: idea.scenes }),
      ...(idea.sceneCount != null && { sceneCount: idea.sceneCount }),
      ...(idea.duration != null && { duration: idea.duration }),
      ...(idea.style != null && { style: idea.style }),
      ...(idea.super != null && { super: idea.super }),
      ...(idea.alternativeSupers != null && { alternativeSupers: idea.alternativeSupers }),
      ...(idea.hook != null && { hook: idea.hook }),
      ...(idea.sequence != null && { sequence: idea.sequence }),
      ...(idea.caption != null && { caption: idea.caption }),
      ...(idea.alternativeHooks != null && { alternativeHooks: idea.alternativeHooks }),
      ...(idea.frames != null && { frames: idea.frames }),
      ...(idea.interaction != null && { interaction: idea.interaction }),
      ...(idea.cta != null && { cta: idea.cta }),
      ...(idea.alternativeCTAs != null && { alternativeCTAs: idea.alternativeCTAs }),
      ...(idea.experience != null && { experience: idea.experience }),
      ...(idea.setup != null && { setup: idea.setup }),
      ...(idea.participation != null && { participation: idea.participation }),
      ...(idea.reveal != null && { reveal: idea.reveal }),
      ...(idea.participantJourney != null && { participantJourney: idea.participantJourney }),
      ...(idea.keyMoments != null && { keyMoments: idea.keyMoments }),
      ...(idea.alternativeReveals != null && { alternativeReveals: idea.alternativeReveals }),
      ...(idea.campaignLine != null && { campaignLine: idea.campaignLine }),
      ...(idea.manifesto != null && { manifesto: idea.manifesto }),
      ...(idea.extensions != null && { extensions: idea.extensions }),
      ...(idea.phases != null && { phases: idea.phases }),
      ...(idea.channels != null && { channels: idea.channels }),
      ...(idea.timeline != null && { timeline: idea.timeline }),
      ...(idea.alternativeLines != null && { alternativeLines: idea.alternativeLines })
    }
  };
}
